import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getMessageTemplates, createMessageTemplate, updateMessageTemplate, deleteMessageTemplate } from '../services/apiMessageTemplates';
import Header from '../components/layout/Header';

export default function MessageTemplatesPage() {
    const queryClient = useQueryClient();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [formData, setFormData] = useState({ keyword: '', content: '' });
    const [error, setError] = useState(null);

    const { data: templates, isLoading } = useQuery({
        queryKey: ['message-templates'],
        queryFn: async () => {
            const res = await getMessageTemplates();
            return res.data;
        }
    });

    const createMutation = useMutation({
        mutationFn: createMessageTemplate,
        onSuccess: () => {
            queryClient.invalidateQueries(['message-templates']);
            closeModal();
        },
        onError: (err) => {
            setError(err.response?.data?.message || 'Error al crear la plantilla. Asegúrate de que la palabra clave sea única.');
        }
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, data }) => updateMessageTemplate(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries(['message-templates']);
            closeModal();
        },
        onError: (err) => {
            setError(err.response?.data?.message || 'Error al actualizar la plantilla.');
        }
    });

    const deleteMutation = useMutation({
        mutationFn: deleteMessageTemplate,
        onSuccess: () => {
            queryClient.invalidateQueries(['message-templates']);
        }
    });

    const openModal = (template = null) => {
        setError(null);
        if (template) {
            setEditingTemplate(template);
            setFormData({ keyword: template.keyword, content: template.content });
        } else {
            setEditingTemplate(null);
            setFormData({ keyword: '', content: '' });
        }
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setEditingTemplate(null);
        setFormData({ keyword: '', content: '' });
        setError(null);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setError(null);
        if (editingTemplate) {
            updateMutation.mutate({ id: editingTemplate.id, data: formData });
        } else {
            createMutation.mutate(formData);
        }
    };

    const handleDelete = (id) => {
        if (window.confirm('¿Estás seguro de eliminar esta plantilla?')) {
            deleteMutation.mutate(id);
        }
    };

    return (
        <div className="flex flex-col h-full bg-slate-50 dark:bg-background-dark">
            <Header title="Plantillas de Respuesta" />
            
            <div className="flex-1 overflow-y-auto p-4 md:p-8 no-scrollbar">
                <div className="max-w-6xl mx-auto space-y-6">
                    
                    <div className="flex justify-between items-center">
                        <div>
                            <h2 className="text-xl font-bold text-text-main dark:text-white">Gestión de Plantillas</h2>
                            <p className="text-sm text-text-secondary">Crea respuestas rápidas usando el disparador @ en el chat.</p>
                        </div>
                        <button 
                            onClick={() => openModal()}
                            className="bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-xl font-bold flex items-center gap-2 shadow-sm transition-all"
                        >
                            <span className="material-symbols-outlined">add</span>
                            Nueva Plantilla
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {isLoading ? (
                            <div className="col-span-full py-12 text-center text-text-secondary">Cargando plantillas...</div>
                        ) : templates?.length === 0 ? (
                            <div className="col-span-full py-20 text-center bg-white dark:bg-surface-dark rounded-3xl border border-dashed border-gray-200 dark:border-gray-800">
                                <span className="material-symbols-outlined text-5xl opacity-20 mb-4">description</span>
                                <p className="text-text-secondary">No hay plantillas creadas. ¡Crea la primera!</p>
                            </div>
                        ) : (
                            templates?.map(template => (
                                <div key={template.id} className="bg-white dark:bg-surface-dark p-6 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm hover:shadow-md transition-shadow group">
                                    <div className="flex justify-between items-start mb-4">
                                        <div className="bg-primary/10 text-primary px-3 py-1 rounded-lg text-xs font-bold uppercase tracking-wider">
                                            @{template.keyword}
                                        </div>
                                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button onClick={() => openModal(template)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg text-gray-500 transition-colors">
                                                <span className="material-symbols-outlined text-[20px]">edit</span>
                                            </button>
                                            <button onClick={() => handleDelete(template.id)} className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg text-red-500 transition-colors">
                                                <span className="material-symbols-outlined text-[20px]">delete</span>
                                            </button>
                                        </div>
                                    </div>
                                    <p className="text-sm text-text-main dark:text-gray-300 line-clamp-4 whitespace-pre-wrap leading-relaxed">
                                        {template.content}
                                    </p>
                                    <div className="mt-4 pt-4 border-t border-gray-50 dark:border-gray-800 flex justify-between items-center">
                                        <span className="text-[10px] text-text-secondary flex items-center gap-1">
                                            <span className="material-symbols-outlined text-[14px]">person</span>
                                            {template.creator?.name || 'Sistema'}
                                        </span>
                                        <span className="text-[10px] text-text-secondary">
                                            {new Date(template.created_at).toLocaleDateString()}
                                        </span>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    <section className="bg-blue-50 dark:bg-blue-900/10 p-6 rounded-2xl border border-blue-100 dark:border-blue-900/30">
                        <h4 className="font-bold text-blue-800 dark:text-blue-300 flex items-center gap-2 mb-2">
                            <span className="material-symbols-outlined">info</span>
                            ¿Cómo usar las variables dinámicas?
                        </h4>
                        <p className="text-sm text-blue-700 dark:text-blue-400 leading-relaxed">
                            Puedes usar etiquetas que se reemplazarán automáticamente al insertar la plantilla:
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                            {[
                                { tag: '{{nombre}}', desc: 'Nombre completo del contacto' },
                                { tag: '{{vendedor}}', desc: 'Tu nombre de usuario' },
                                { tag: '{{empresa}}', desc: 'Nombre de la empresa del contacto' }
                            ].map(item => (
                                <div key={item.tag} className="bg-white dark:bg-slate-900/50 px-3 py-2 rounded-lg border border-blue-100 dark:border-blue-800/50 shadow-sm">
                                    <code className="text-primary font-bold text-xs">{item.tag}</code>
                                    <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">{item.desc}</p>
                                </div>
                            ))}
                        </div>
                    </section>
                </div>
            </div>

            {/* Create/Edit Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={closeModal} />
                    <form 
                        onSubmit={handleSubmit}
                        className="relative z-10 bg-white dark:bg-surface-dark rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-200"
                    >
                        <div className="p-6 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center">
                            <h3 className="text-xl font-bold text-text-main dark:text-white">
                                {editingTemplate ? 'Editar Plantilla' : 'Nueva Plantilla'}
                            </h3>
                            <button type="button" onClick={closeModal} className="text-gray-400 hover:text-gray-600 transition-colors">
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>
                        
                        <div className="p-6 space-y-4">
                            {error && (
                                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 text-xs rounded-xl flex items-center gap-2 animate-in fade-in">
                                    <span className="material-symbols-outlined text-[18px]">error</span>
                                    {error}
                                </div>
                            )}
                            <div>
                                <label className="block text-sm font-bold text-text-main dark:text-gray-200 mb-1.5">
                                    Palabra Clave (Keyword)
                                </label>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-primary font-bold">@</span>
                                    <input 
                                        type="text"
                                        required
                                        value={formData.keyword}
                                        onChange={(e) => setFormData({ ...formData, keyword: e.target.value.replace(/\s+/g, '-').toLowerCase() })}
                                        className="w-full bg-gray-50 dark:bg-background-dark border border-gray-200 dark:border-gray-700 rounded-xl pl-8 pr-4 py-2.5 text-sm focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                                        placeholder="ej. saludo-inicial"
                                    />
                                </div>
                                <p className="text-[10px] text-text-secondary mt-1.5 px-1">Sin espacios. Se usará para buscar la plantilla en el chat.</p>
                            </div>

                            <div>
                                <label className="block text-sm font-bold text-text-main dark:text-gray-200 mb-1.5">
                                    Contenido del Mensaje
                                </label>
                                <textarea 
                                    required
                                    rows={6}
                                    value={formData.content}
                                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                                    className="w-full bg-gray-50 dark:bg-background-dark border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-primary/20 outline-none transition-all resize-none"
                                    placeholder="Hola {{nombre}}, ¿cómo estás?..."
                                />
                            </div>
                        </div>

                        <div className="p-6 bg-gray-50 dark:bg-gray-800/50 flex gap-3">
                            <button 
                                type="button"
                                onClick={closeModal}
                                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-bold text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                            >
                                Cancelar
                            </button>
                            <button 
                                type="submit"
                                disabled={createMutation.isPending || updateMutation.isPending}
                                className="flex-1 px-4 py-2.5 rounded-xl bg-primary text-white text-sm font-bold hover:bg-primary/90 transition-all shadow-md disabled:opacity-50"
                            >
                                {editingTemplate ? 'Guardar Cambios' : 'Crear Plantilla'}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}
